import m from 'mithril'
import { css } from '@emotion/css'
import { colors, font, space, card, input, btnPrimary, btnSecondary } from '../styles/theme.js'
import { clusters as clustersApi, tags as tagsApi, projects as projectsApi } from '../lib/api.js'

// --- Styles ---

const pageWrap = css`
  display: flex;
  flex-direction: column;
  gap: ${space.lg};
`

const breadcrumb = css`
  font-size: ${font.size.sm};
  color: ${colors.textMuted};
  a { color: ${colors.textSecondary}; }
`

const headerRow = css`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${space.md};
`

const labelEdit = css`
  ${input};
  font-size: ${font.size.xl};
  font-weight: ${font.weight.bold};
  padding: ${space.xs} ${space.sm};
`

const sessionList = css`
  border: 1px solid ${colors.border};
  border-radius: 8px;
  background: ${colors.bgCard};
  overflow: hidden;
`

const sessionRow = css`
  display: flex;
  align-items: center;
  padding: ${space.md} ${space.lg};
  border-bottom: 1px solid ${colors.border};
  cursor: pointer;
  transition: background 0.1s ease;
  &:last-child { border-bottom: none; }
  &:hover { background: ${colors.bg}; }
`

const sessionInfo = css`
  flex: 1;
  min-width: 0;
`

const sessionId = css`
  font-size: ${font.size.sm};
  font-weight: ${font.weight.medium};
  color: ${colors.text};
`

const sessionMeta = css`
  font-size: ${font.size.xs};
  color: ${colors.textMuted};
  margin-top: 2px;
  display: flex;
  gap: ${space.md};
`

const distanceBadge = css`
  font-size: ${font.size.xs};
  color: ${colors.textMuted};
  white-space: nowrap;
  margin-left: ${space.md};
`

const bulkActions = css`
  display: flex;
  align-items: center;
  gap: ${space.sm};
  padding: ${space.md};
  background: ${colors.bgSidebar};
  border-radius: 8px;
`

const emptyState = css`
  text-align: center;
  padding: ${space['2xl']};
  color: ${colors.textMuted};
`

// --- Component ---

export function ClusterDetailView() {
  let projectId = null
  let project = null
  let clusterId = null
  let cluster = null
  let allTags = []
  let loading = false
  let editing = false
  let editLabel = ''
  let bulkTagId = ''

  async function loadCluster() {
    loading = true
    m.redraw()
    try {
      ;[cluster, allTags] = await Promise.all([
        clustersApi.get(projectId, clusterId),
        tagsApi.list(projectId),
      ])
      editLabel = cluster?.label || ''
    } catch (err) {
      console.error('Failed to load cluster:', err)
    } finally {
      loading = false
      m.redraw()
    }
  }

  async function saveLabel() {
    try {
      cluster = await clustersApi.update(projectId, clusterId, { label: editLabel })
      editing = false
      m.redraw()
    } catch (err) {
      console.error('Failed to save label:', err)
    }
  }

  function uniqueSessionIds() {
    if (!cluster) return []
    return [...new Set(cluster.pairs.map(p => p.session_id))]
  }

  function goToSession(sessionId) {
    if (!cluster) return
    const ids = uniqueSessionIds()
    try {
      sessionStorage.setItem('browserContext', JSON.stringify({
        projectId,
        origin: 'cluster',
        clusterId,
        sessionIds: ids,
        total: ids.length,
        page: 0,
        pageSize: ids.length,
      }))
    } catch { /* ignore */ }
    m.route.set(`/projects/${projectId}/sessions/${sessionId}`)
  }

  async function bulkApplyTag() {
    if (!bulkTagId || !cluster) return
    try {
      for (const sid of uniqueSessionIds()) {
        await tagsApi.assign(projectId, sid, bulkTagId)
      }
      await loadCluster()
    } catch (err) {
      console.error('Failed to bulk apply tag:', err)
    }
  }

  return {
    oninit(vnode) {
      projectId = vnode.attrs.projectId
      clusterId = vnode.attrs.clusterId
      projectsApi.get(projectId).then(p => { project = p; m.redraw() }).catch(() => {})
      loadCluster()
    },

    view(vnode) {
      projectId = vnode.attrs.projectId
      clusterId = vnode.attrs.clusterId

      if (loading) {
        return m('div', { class: emptyState }, 'Loading...')
      }

      if (!cluster) {
        return m('div', { class: emptyState }, 'Cluster not found')
      }

      return m('div', { class: pageWrap }, [
        m('div', { class: breadcrumb }, [
          m(m.route.Link, { href: '/projects' }, 'Projects'),
          ' / ',
          m(m.route.Link, { href: `/projects/${projectId}` }, project?.name || 'Project'),
          ' / ',
          m(m.route.Link, { href: `/projects/${projectId}/clusters` }, 'Clusters'),
          ' / ',
          cluster.label || 'Cluster',
        ]),

        // Header with editable label
        m('div', { class: headerRow }, [
          editing
            ? m('div', { style: 'display: flex; gap: 8px; flex: 1;' }, [
                m('input', {
                  class: labelEdit,
                  value: editLabel,
                  oninput: (e) => { editLabel = e.target.value },
                  onkeydown: (e) => { if (e.key === 'Enter') saveLabel() },
                }),
                m('button', { class: btnPrimary, onclick: saveLabel }, 'Save'),
                m('button', { class: btnSecondary, onclick: () => { editing = false } }, 'Cancel'),
              ])
            : m('div', { style: 'display: flex; align-items: center; gap: 8px;' }, [
                m('h1', {
                  style: `font-size: ${font.size['2xl']}; font-weight: ${font.weight.bold}; cursor: pointer;`,
                  onclick: () => { editing = true; editLabel = cluster.label || '' },
                  title: 'Click to edit',
                }, cluster.label || 'Unlabeled cluster'),
                m('span', { style: `font-size: ${font.size.sm}; color: ${colors.textMuted};` },
                  `${cluster.pair_count} pairs from ${cluster.session_count} sessions`),
              ]),
        ]),

        // Bulk actions
        m('div', { class: bulkActions }, [
          m('span', { style: `font-size: ${font.size.sm}; color: ${colors.textSecondary};` },
            'Bulk apply tag:'),
          allTags.length > 0
            ? m('select', {
                style: `padding: 4px 8px; border: 1px solid ${colors.border}; border-radius: 4px; font-family: ${font.family}; font-size: ${font.size.sm};`,
                value: bulkTagId,
                onchange: (e) => { bulkTagId = e.target.value },
              }, [
                m('option', { value: '' }, 'Select tag...'),
                ...allTags.map(t => m('option', { value: t.id }, t.name)),
              ])
            : m('span', { style: `font-size: ${font.size.sm}; color: ${colors.textMuted};` },
                'No tags defined'),
          m('button', {
            class: btnSecondary,
            disabled: !bulkTagId,
            onclick: bulkApplyTag,
          }, `Apply to all ${cluster.session_count} sessions`),
        ]),

        // Pair list
        m('div', { class: sessionList },
          cluster.pairs.length === 0
            ? m('div', { class: emptyState }, 'No Q&A pairs in this cluster')
            : cluster.pairs.map(pair =>
                m('div', {
                  key: pair.pair_id,
                  class: sessionRow,
                  onclick: () => goToSession(pair.session_id),
                }, [
                  m('div', { class: sessionInfo }, [
                    m('div', { class: sessionId },
                      pair.userPreview || '(no preview)'),
                    m('div', { class: sessionMeta }, [
                      m('span', pair.external_id || pair.session_id.slice(0, 8)),
                    ]),
                  ]),
                  pair.distance != null
                    ? m('div', { class: distanceBadge },
                        `dist: ${pair.distance.toFixed(3)}`)
                    : null,
                ])
              )
        ),
      ])
    },
  }
}
